import * as React from 'react';
import { theme } from '../../theme/theme';
import { Button,Typography, Grid, TextField, Switch, FormControl, FormControlLabel, FormGroup } from '@mui/material';
import {Box} from '@mui/system';
import TreeView from '@mui/lab/TreeView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import TreeItem,{ treeItemClasses } from '@mui/lab/TreeItem';
import { styled } from '@mui/system';
import { RemoteFileTreeType } from './workspace';

const DarkSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase': {
      color: theme.palette.sidebarContent.labelText,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: theme.palette.sidebarContent.valueText,
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.sidebarContent.valueText,
    },
  }));

const RemoteFileTree = (props) => {    

    const StyledTreeItem = styled(TreeItem)(({ theme }) => ({
        [`& .${treeItemClasses.label}`]: {
            fontWeight: '900',
            fontFamily: 'Open Sans',
            fontSize: '18px',              
            letterSpacing: '0px',
            color: theme.palette.primary.contrastText
        }
      }));

    const [searchText, setSearchText] = React.useState('');
    const [expandAll, setExpandAll] = React.useState(false);
    const [expandedNodes, setExpandedNodes] = React.useState(props.type === RemoteFileTreeType.Configuration ? [] : [props.data.id]);

    let filteredChildren = null;
    if (props.type === RemoteFileTreeType.Configuration) {
        filteredChildren = props.data.children.filter(x => {
            return x.name.toLowerCase().includes(searchText.toLowerCase()) || x.name === 'All';
        })
    }

    let allNodeIds = []
    if (filteredChildren) {
        allNodeIds = filteredChildren.map(x => x.id)
    }

    React.useEffect(() => {
        // Toggle the expanded switch state (harmless if the switch is not used)
        if (allNodeIds.every(x => expandedNodes.includes(x))) {
            setExpandAll(true)
        } else {
            setExpandAll(false)
        }
    }, [allNodeIds, expandedNodes])

    const handleSearchTextChanged = (event) => {
        // client-side search with new text
        setSearchText(event.target.value);
    };

    const handleExpandAllChanged = (event) => {
        setExpandAll(event.target.checked);

        // Update the expanded nodes
        if (event.target.checked) {
            setExpandedNodes(allNodeIds)
        } else {
            setExpandedNodes([])
        }
    }

    const handleExpandedChanged = (event, ids) => {
        setExpandedNodes(ids);
        
        // Toggle the expanded switch state (harmless if the switch is not used)
        if (allNodeIds.every(x => expandedNodes.includes(x)) && expandedNodes.every(x => allNodeIds.includes(x))) {
            setExpandAll(true)
        } else {
            setExpandAll(false)
        }
    }

    /**
     * @typedef FileTreeViewModel
     * @type {object}
     * @property {string} id - The file/folder ID
     * @property {string} name - The file/folder name
     * @property {boolean} isActionableItem - Is the actions button allowed on this node?
     * @property {FileTreeViewModel[]} children - child nodes
     */
    const renderBranch = (node) => {
        if(node)
        {
            if(node.isActionableBranch)
            {
                return (<React.Fragment key={node.id}>
                        <div style={{backgroundColor:'#112334',height:'2px',marginBottom:'10px', marginTop:'5px',width:'100%'}}/>
                        <StyledTreeItem nodeId={node.id} 
                            label={(<><Grid container direction='row' style={{marginBottom:'5px',paddingRight:35}}>
                                        <Grid item xs={true}>
                                            <Typography key={node.id +"name"} sx={{                                                    
                                                color: theme.palette.primary.contrastText, 
                                                marginTop:'8px',                    
                                                marginBottom:'8px',
                                                marginLeft:'10px'
                                                }}
                                            >
                                                {node.name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <Button key={node.id + "button"} onClick={() => {props.action(node.id,node.name,false,"Software")}} 
                                                variant='contained' 
                                                sx={{
                                                    backgroundColor: theme.palette.error.main,
                                                    color: theme.palette.primary.contrastText, 
                                                    ml: 2, 
                                                    height:'30px',
                                                    width:'95px',
                                                    fontWeight: '600',
                                                    textTransform:'none',
                                                    fontFamily: 'Open Sans',
                                                    fontSize: '15px',   
                                                    letterSpacing: '0.53px'
                                                }}>
                                                    Delete
                                            </Button>
                                        </Grid>
                                    </Grid></>)} sx={{ 
                                        textAlign:'left',
                                    }}>  
                                        { node.children ? node.children.map((node) => renderNode(node)) : null }
                        </StyledTreeItem></React.Fragment>)
            }     
            else if(node.children && node.children.length > 0) {
                return (<React.Fragment key={node.id}>
                        <div style={{backgroundColor:'#112334',height:'2px',marginBottom:'5px',width:'100%'}}/>
                        <StyledTreeItem nodeId={node.id} label={node.name} sx={{ 
                            textAlign:'left',
                        }}>
                            {node.children ? node.children.map((node) => renderNode(node)) : null}
                        </StyledTreeItem>
                        </React.Fragment>)
            } 
            else {
                return null
            }
        }
        else {
            return null
        }
    };

    const renderLeaf = (node) => {
        if(node)
        {
            return (<Grid container direction='row' key={node.id} style={{marginBottom:'5px', paddingRight:40}}>
                <Grid item xs={true}>
                    <Typography sx={{              
                        fontWeight: '900',
                        fontFamily: 'Open Sans',
                        fontSize: '18px',        
                        letterSpacing: '0px',
                        color: '#AEB1B4',
                        marginTop:'8px',                    
                        marginBottom:'8px',
                        marginLeft:'10px'
                        }}
                    >
                        &nbsp;&nbsp; &#x2022; {node.name}
                    </Typography>
                </Grid>
                <Grid item xs={2}>
                    <Button 
                        onClick={() => {props.action(node.id,node.name,!node.targetSpecificActionsOnly,props.type == RemoteFileTreeType.Software ? "Software" : "Configuration")}} 
                        variant='contained' 
                        sx={{
                            backgroundColor: theme.palette.tertiary.main, 
                            color: theme.palette.primary.contrastText, 
                            ml: 2, 
                            height:'35px',
                            width:'95px',
                            textTransform:'none',                            
                            fontWeight: '600',
                            fontFamily: 'Open Sans',
                            fontSize: '15px',                          
                            letterSpacing: '0.53px'
                        }}>
                        Actions
                    </Button>
                </Grid>
            </Grid> );
        }     
        else
        {
            return null;
        }
    };

    const renderNode = (node) => {
        if(node)
            return node.isLeaf ? renderLeaf(node) : renderBranch(node);
        return null;
    };

    return (
        <>
        <Typography sx={{
            color: theme.palette.primary.contrastText,
            fontFamily:'Open Sans',
            fontSize: '28px',            
            letterSpacing: '0.56px',
            paddingBottom: '30px'
            }}>{props.type == RemoteFileTreeType.Software ? "Software Updates" : "Configurations"}</Typography>
            <Box sx={{ backgroundColor:'#0A1826',borderRadius:'10px',textAlign:'center', verticalAlign:'middle', minHeight:150}}>
            {props.data == null || 
                    props.data == undefined ||
                    props.data.children == null ||
                    props.data.children == undefined ||
                    props.data.children.length == 0 ? (
                        <Typography sx={{
                            color: theme.palette.primary.contrastText, 
                            fontWeight: '900',
                            fontFamily: 'Open Sans',
                            fontSize: '18px',
                            paddingTop:'65px',
                            letterSpacing: '0px',}}>No Packages Found</Typography>
                    ): (
                        <>
                        {props.type === RemoteFileTreeType.Configuration && (
                            <Grid container direction={'row'}>
                                <TextField 
                                    onChange={handleSearchTextChanged}
                                    placeholder={'Search'}
                                    value={searchText}
                                    InputProps={{
                                        startAdornment: (
                                        <SearchIcon
                                            sx={{ color: theme.palette.primary.contrastText }}
                                        />
                                        ),
                                    }}
                                    sx={{
                                        flexGrow: 1,
                                        "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
                                        "& .MuiOutlinedInput-input.Mui-disabled":{WebkitTextFillColor: 'white'},
                                        input: { 
                                        color: theme.palette.primary.contrastText, 
                                        }, 
                                    }}
                                />
                                <FormControl component="fieldset" style={{marginRight:10, justifyContent:'center'}}>
                                    <FormGroup aria-label="position" row>
                                        <FormControlLabel
                                            value="showaircraft"
                                            control={<DarkSwitch checked={expandAll} onChange={handleExpandAllChanged} />}
                                            label="Expand All"
                                            labelPlacement="start"
                                            sx={{
                                            color: theme.palette.sidebarContent.valueText,
                                            "& .MuiFormControlLabel-label": {fontSize: 14},
                                            }}
                                        />
                                    </FormGroup>
                                </FormControl>
                            </Grid>
                        )}
                        <TreeView
                            aria-label="rich object"
                            defaultExpanded={props.type == RemoteFileTreeType.Software ? ["All Software"] : [] }
                            defaultCollapseIcon={<ExpandMoreIcon />}                            
                            defaultExpandIcon={<ChevronRightIcon />}
                            expanded={expandedNodes}
                            onNodeToggle={handleExpandedChanged}
                            sx={{ flexGrow: 1, width:'100%', maxWidth:'100%', overflowY: 'auto',color: theme.palette.primary.contrastText }}>
                                {props.type == RemoteFileTreeType.Software ? renderNode(props.data) : filteredChildren.map((x) => renderNode(x))}
                        </TreeView>
                        </>
                    )}
            </Box>
        </>
    );
}

export default RemoteFileTree;